import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { LANGUAGE_KEY } from "../Services/sessionStorage";
import { fetchLanguages } from "../Services/apiService";
import { translations, SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from "./translations";

// Used only if GET /languages hasn't resolved yet or the request fails —
// keeps the switcher functional (UI-only, code-based) even then. `id: null`
// entries just can't be saved as a profile preference until the real list
// loads; setLanguage() itself only ever needs the code.
const FALLBACK_LANGUAGES = SUPPORTED_LANGUAGES.map((code) => ({
  id: null,
  code,
  name: LANGUAGE_LABELS[code],
  native_name: LANGUAGE_LABELS[code],
}));

const LanguageContext = createContext();

function detectInitialLanguage() {
  const stored = localStorage.getItem(LANGUAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;

  const browser = (navigator.language || "").slice(0, 2).toLowerCase();
  return SUPPORTED_LANGUAGES.includes(browser) ? browser : "en";
}

function resolve(dictionary, path) {
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), dictionary);
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (match, key) => (vars[key] !== undefined ? vars[key] : match));
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(detectInitialLanguage);
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);

  useEffect(() => {
    let cancelled = false;
    fetchLanguages()
      .then((res) => {
        if (!cancelled && Array.isArray(res?.data) && res.data.length > 0) {
          setLanguages(res.data);
        }
      })
      .catch(() => {
        // Keep FALLBACK_LANGUAGES — the switcher stays usable, just can't
        // persist a preference (no real ids) until this succeeds.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_KEY, lang);
  }, []);

  const t = useCallback(
    (path, vars) => {
      const value =
        resolve(translations[language], path) ??
        resolve(translations.en, path) ??
        path;
      return typeof value === "string" ? interpolate(value, vars) : value;
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, supportedLanguages: SUPPORTED_LANGUAGES, languages }),
    [language, setLanguage, t, languages]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
export const useTranslation = () => useContext(LanguageContext);
